<template>
    <div class="row">
        <div class="col">
            <div class="card text-sm pb-5">
                <div class="card-body">
                    <h5 class="mb-0">Test results</h5> 
                    
                    <div v-if="!Object.keys(results).length || test.inReviewStatus != null || (results.t1Invalidated && results.t2Invalidated)" class="alert alert-warning mt-3">
                        Test results are not available for this test
                    </div>

                    <div v-if="test.notificationDate != null" class="alert alert-warning mt-3">
                        These results exceed your notification thresholds
                    </div>

                    <div>
                        <div class="row mt-4">
                            <div class="col-lg-6 pr-lg-6">
                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Test no</div>
                                    <div class="col-sm-9">{{ test.testNumber }}</div>
                                </div>

                                <hr>

                                <div v-if="test.deviceNumber && isTrial">
                                    <div class="row">
                                        <div class="col-sm-3 font-weight-medium">Device number</div>
                                        <div class="col-sm-9">{{ test.deviceNumber }}</div>
                                    </div>

                                    <hr>
                                </div>

                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Facility name</div>
                                    <div class="col-sm-9">{{ test.facilityName }}</div>
                                </div>

                                <hr>

                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Assigned HCP</div>
                                    <div class="col-sm-9">{{ test.practitionerName }}</div>
                                </div>

                                <div v-if="test.createdByPractitionerName">
                                    <hr>

                                    <div class="row">
                                        <div class="col-sm-3 font-weight-medium">Created by</div>
                                        <div class="col-sm-9">{{ test.createdByPractitionerName }}</div>
                                    </div>
                                </div>

                                <hr>

                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Date assigned</div>
                                    <div class="col-sm-9">{{ formatDateGeneral(test.issuedDate) }}</div>
                                </div>

                                <hr>

                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Assigned method</div>
                                    <div class="col-sm-9">
                                        <span v-if="test.method == IssueMethod.IN_PERSON">In person</span>
                                        <span v-if="test.method == IssueMethod.POST">Post</span>
                                        <span v-if="test.method == IssueMethod.COLLECTION">Collection</span>
                                    </div>
                                </div>

                                <div v-if="test.language || test.alernativeLanguage">
                                    <hr>

                                    <div class="row">
                                        <div class="col-sm-3 font-weight-medium">Leaflet and communications language</div>
                                        <div class="col-sm-9">{{ formatLanguageLabel(test.language || test.alernativeLanguage) }}</div>
                                    </div>
                                </div>
                            </div>

                            <div class="col-lg-6 pl-lg-6 mt-4 mt-md-0">
                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Testing for</div>
                                    <div class="col-sm-9">
                                        <span v-if="test.reason == TestIssueReason.GD">Gestational Diabetes Mellitus</span>
                                        <span v-if="test.reason == TestIssueReason.CF">Cystic Fibrosis</span>
                                        <span v-if="test.reason == TestIssueReason.TYPE1">Type 1</span>
                                        <span v-if="test.reason == TestIssueReason.TYPE2">Type 2</span>
                                        <span v-if="test.reason == TestIssueReason.RESEARCH_STUDY">Research Study</span>
                                    </div>
                                </div>

                                <hr>

                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Status</div>
                                    <div class="col-sm-9">
                                        <testStatuses :status="test.status" />
                                    </div>
                                </div>

                                <hr>

                                <div v-if="test.status == TestStatus.CANCELLED && test.cancelledReasonType != null">
                                    <div class="row">
                                        <div class="col-sm-3 font-weight-medium">Cancelled reason type</div>
                                        <div class="col-sm-9">{{ formatCancelledReasonType(test.cancelledReasonType) }}</div>
                                    </div>

                                    <hr>
                                </div>

                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Test created</div>
                                    <div class="col-sm-9">{{ test.dateCreated | moment("DD/MM/YYYY HH:mm") }}</div>
                                </div>

                                <hr>

                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Test date</div>
                                    <div class="col-sm-9">{{ test.dueDate | moment("DD/MM/YYYY") }}</div>
                                </div>
                            </div>
                        </div>

                        <div v-if="test.testReasons && test.testReasons.length" class="mt-4">
                            <hr class="my-4" />
                            <h5 class="mb-0">Risk factors</h5>
                            <ul class="list-unstyled with-ticks mt-4" style="column-count: 3">
                                <li v-for="(reason, index) in test.testReasons" :key="index" class="mb-3">{{ getReasonLabel(reason) }}</li>
                            </ul>
                        </div>

                        <div v-if="test.ccEmails != null && test.ccEmails.length > 0" class="mt-4">
                            <hr class="my-4" />
                            <h5 class="mb-0">Notify teams</h5>
                            <ul class="list-unstyled with-ticks mt-4" style="column-count: 3">
                                <li v-for="ccEmail in test.ccEmails" :key="ccEmail" class="mb-3">{{ getTeamLabel(ccEmail) }}</li>
                            </ul>
                        </div>

                        <div v-if="Object.keys(results).length && test.inReviewStatus == null" class="mt-4">
                            <hr class="my-4" />

                            <div class="row" v-if="results.hasResults">                    
                                <div class="col-md-6 pr-lg-6">
                                    <h6>Fasting</h6>

                                    <div v-if="!showTestResult(results.testAValue, results.testATime, results.t1Invalidated)" class="test-meta p-3 mt-4">
                                        <div class="row">
                                            <div class="col-sm-3 font-weight-medium">Result</div>
                                            <div class="col-sm-9">Unavailable</div>
                                        </div>
                                    </div>
                                    <div v-else class="test-meta p-3 mt-4">
                                        <div class="row">
                                            <div class="col-sm-3 font-weight-medium">Result</div>
                                            <div class="col-sm-9">{{ calculateTestResult(results.testAResult) }}</div>
                                        </div>

                                        <div v-if="test.deviceDateFaulty !== true">
                                            <hr>

                                            <div class="row">
                                                <div class="col-sm-3 font-weight-medium">Completed</div>
                                                <div class="col-sm-9">{{ results.testATime | moment("DD/MM/YYYY HH:mm") }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-md-6 pl-lg-6 mt-4 mt-md-0">
                                    <h6>2 hour</h6>

                                    <div v-if="!showTestResult(results.testBValue, results.testBTime, results.t2Invalidated)" class="test-meta p-3 mt-4">
                                        <div class="row">
                                            <div class="col-sm-3 font-weight-medium">Result</div>
                                            <div class="col-sm-9">Unavailable</div>
                                        </div>
                                    </div>
                                    <div v-else class="test-meta p-3 mt-4">
                                        <div class="row">
                                            <div class="col-sm-3 font-weight-medium">Result</div>
                                            <div class="col-sm-9">{{ calculateTestResult(results.testBResult) }}</div>
                                        </div>

                                        <div v-if="test.deviceDateFaulty !== true">
                                            <hr>
                                            
                                            <div class="row">
                                                <div class="col-sm-3 font-weight-medium">Completed</div>
                                                <div class="col-sm-9">{{ results.testBTime | moment("DD/MM/YYYY HH:mm") }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="row mt-4">
                                <div class="col-md-6 pr-lg-6">
                                    <h6>Device used for these results</h6>

                                    <div class="test-meta p-3 mt-4">
                                        <div class="row">
                                            <div class="col-sm-3 font-weight-medium">Device serial number</div>
                                            <div class="col-sm-9">{{ results.serialNumber || 'Not recorded' }}</div>
                                        </div>

                                        <div v-if="results.details.softwareVersion">
                                            <hr>

                                            <div class="row">
                                                <div class="col-sm-3 font-weight-medium">Software version</div>
                                                <div class="col-sm-9">{{ results.details.softwareVersion }}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="results.statusChangedTo != TestStatus.RETURNED && results.hasErrors && results.validationErrors.length" class="mt-4">
                                        <h6>Validation Errors</h6>
                                        <b-list-group class="mt-4">
                                            <b-list-group-item v-for="(item, index) in results.validationErrors" :key="index" class="text-danger font-weight-bold">{{ `${item}: ${ValidationErrors[item]}` }}</b-list-group-item>
                                        </b-list-group>
                                    </div>

                                    <div v-if="results.statusChangedTo != TestStatus.RETURNED && results.hasErrors && results.errorFlags.length" class="mt-4">
                                        <h6>Error Flags</h6>
                                        <b-list-group class="mt-4">
                                            <b-list-group-item v-for="(item, index) in results.errorFlags" :key="index" class="text-danger font-weight-bold">{{ `${item}: ${ErrorFlags[item]}` }}</b-list-group-item>
                                        </b-list-group>
                                    </div>
                                </div>

                                <div v-if="isTrial" class="col-md-6 pl-lg-6 mt-4 mt-md-0">
                                    <h6>Raw Data</h6>

                                    <button @click="openRawDataModal()" class="btn btn-secondary sm-shadow mt-4">Raw Data</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mapGetters} from 'vuex'
import TestStatuses from '../../../Common/components/TestStatusesComponent.vue'
import RawDataModal from '@/components/modals/RawDataModalComponent.vue'
import ValidationErrors from '../../../Common/errors/ValidationErrors'
import ErrorFlags from '../../../Common/errors/ErrorFlags'
import TestIssueReason from '../../../Common/enums/TestIssueReason'
import TestStatus from '../../../Common/enums/TestStatus'
import IssueMethod from '../../../Common/enums/IssueMethod'
import FormatterMixin from '../../../Common/mixins/FormatterMixin'
export default {
    name: 'TestResultsComponent', 

    mixins: [
        FormatterMixin
    ],

    components: {
        TestStatuses
    },

    props: {
        test: Object,
        results: Object
    },

    data() {
        return {
            RawDataModal,
            ValidationErrors,
            ErrorFlags,
            TestIssueReason,
            TestStatus,
            IssueMethod
        }
    },

    computed: {
        ...mapGetters({
            organisationSettingsMap: 'OrganisationStore/GetOrganisationSettingsMap',
            isTrial: 'OrganisationStore/GetIsTrial',
            myOrganisationSettings: 'OrganisationStore/GetMyOrganisationSettings'
        })
    },

    methods: {
        openRawDataModal() {
            this.$modal.show(RawDataModal, {data: this.results})
        },

        calculateTestResult(val) {
            let result = (val * 1000)

            if(result < 3.5) return '< 3.5 mmol/l'

            if(result > 15) return '> 15'

            result = result.toFixed(1)

            return result + ' mmol/l'
        },

        showTestResult(testResultValue, testResultDateTime, invalidated) {

            if (invalidated === true) return false

            if (this.$moment(testResultDateTime) > this.$moment().add(1, 'days')) return false

            let result = testResultValue * 1000000000

            if(result > 100000) return false

            return true
        },

        getReasonLabel(reason) {
            var found = this.organisationSettingsMap.find(x => x.key == reason)
            if(found == null) return reason
            return found.label
        },

        getTeamLabel(email) {
            if (this.myOrganisationSettings.ccEmails != null && email != null){
                let label = "";
                Object.entries(this.myOrganisationSettings.ccEmails).map(
                    ([key, value]) => {
                        if (value === email){
                            label = key;
                        }
                    }
                )
                return label
            }
            return email
        }

    }
}
</script>